import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Banknote, ArrowLeft, LogIn, UserPlus, Zap } from 'lucide-react';
import PreApprovalCheck from '@/components/PreApprovalCheck';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientLogin() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotSending(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: forgotEmail, reset_base: window.location.origin });
      setForgotSent(true);
    } catch (err) { toast.error('Something went wrong — please try again'); }
    setForgotSending(false);
  };

  const [registerData, setRegisterData] = useState({
    full_name: '',
    email: '',
    phone: '',
    id_number: '',
    password: '',
    confirmPassword: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const user = await login(loginData.email, loginData.password);
      if (user.role === 'client') {
        toast.success('Welcome back!');
        const redirect = new URLSearchParams(window.location.search).get('redirect');
        navigate(redirect || '/client/dashboard');
      } else if (user.role === 'sales_agent') {
        toast.success('Welcome back!');
        navigate('/agent/dashboard');
      } else {
        toast.error('Please use the admin login');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { confirmPassword, ...userData } = registerData;
      await register(userData);
      toast.success('Account created successfully!');
      const redirect = new URLSearchParams(window.location.search).get('redirect');
      navigate(redirect || '/client/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Home */}
        <Link 
          to="/" 
          className="inline-flex items-center text-emerald-700 hover:text-emerald-900 mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2">
            <div className="w-12 h-12 bg-emerald-900 rounded-xl flex items-center justify-center">
              <Banknote className="w-7 h-7 text-white" />
            </div>
            <span className="text-3xl font-bold text-emerald-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              2k2day
            </span>
          </div>
          <p className="text-slate-600 mt-2">Client Portal</p>
        </div>

        <Card className="shadow-xl border-slate-100">
          <Tabs defaultValue="login" className="w-full">
            <CardHeader className="pb-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="login-tab">
                  <LogIn className="w-4 h-4 mr-2" />
                  Login
                </TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Register
                </TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent>
              {/* Login Form */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="your@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 rounded-full bg-emerald-900 hover:bg-emerald-800"
                    disabled={isLoading}
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? <div className="spinner"></div> : 'Sign In'}
                  </Button>
                </form>
                <div className="mt-3 text-center">
                  <button type="button" className="text-sm text-emerald-800 hover:underline" onClick={() => { setShowForgot(v => !v); setForgotSent(false); }} data-testid="forgot-password-link">
                    Forgot your password?
                  </button>
                </div>
                {showForgot && (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="forgot-password-panel">
                    {forgotSent ? (
                      <p className="text-sm text-emerald-700 text-center py-2" data-testid="forgot-sent-message">If that email is registered, a reset link has been sent to it. Check your inbox (and spam folder).</p>
                    ) : (
                      <form onSubmit={handleForgot} className="space-y-2">
                        <Label className="text-xs">Enter your email and we'll send you a reset link</Label>
                        <Input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="your@email.com" required data-testid="forgot-email-input" />
                        <Button type="submit" size="sm" className="w-full rounded-full bg-emerald-900 hover:bg-emerald-800" disabled={forgotSending || !forgotEmail} data-testid="forgot-submit-btn">
                          {forgotSending ? 'Sending…' : 'Send Reset Link'}
                        </Button>
                      </form>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* Register Form */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <Input
                      id="register-name"
                      placeholder="John Doe"
                      value={registerData.full_name}
                      onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="your@email.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-phone">Phone</Label>
                      <Input
                        id="register-phone"
                        placeholder="082 123 4567"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                        required
                        data-testid="register-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-id">ID Number</Label>
                      <Input
                        id="register-id"
                        placeholder="9001015009087"
                        value={registerData.id_number}
                        onChange={(e) => setRegisterData({ ...registerData, id_number: e.target.value })}
                        required
                        data-testid="register-id-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      data-testid="register-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-confirm">Confirm Password</Label>
                    <Input
                      id="register-confirm"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                      required
                      data-testid="register-confirm-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 rounded-full bg-emerald-900 hover:bg-emerald-800"
                    disabled={isLoading}
                    data-testid="register-submit-btn"
                  >
                    {isLoading ? <div className="spinner"></div> : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

        {/* Instant Pre-Approval */}
        <Card className="shadow-xl border-slate-100 mt-6" data-testid="login-preapproval">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-emerald-900">
              <Zap className="w-5 h-5 text-emerald-700" /> Instant Pre-Approval
            </CardTitle>
            <CardDescription>Check in seconds whether you could qualify — no documents needed, no impact on your record.</CardDescription>
          </CardHeader>
          <CardContent>
            <PreApprovalCheck />
          </CardContent>
        </Card>

        <p className="text-center mt-4">
          <Link to="/install" className="text-sm text-emerald-700 hover:underline font-medium" data-testid="get-app-link">
            📱 Get the 2k2day app on your phone
          </Link>
        </p>
      </div>
    </div>
  );
}
